import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Microscope, FlaskConical, ShieldAlert, Layers, ArrowRight } from "lucide-react";
import { Section, SectionHeading, Eyebrow } from "@/components/Section";

const TESTS = [
  { icon: FlaskConical, t: "Potency Analysis", d: "We quantify active ingredients to verify how much is really present versus what the label claims." },
  { icon: ShieldAlert, t: "Heavy Metals Screen", d: "Detection of lead, arsenic, cadmium and mercury — common contaminants in poorly sourced products." },
  { icon: Layers, t: "Peptide Analysis", d: "Identity and purity testing for GLP-1 and peptide products using LC-MS/MS." },
  { icon: Microscope, t: "Fillers & Allergens", d: "Identification of undeclared fillers, adulterants and common allergens." },
];

export default function OurTesting() {
  return (
    <div data-testid="our-testing-page">
      <section className="bg-ocean">
        <div className="mx-auto max-w-7xl px-6 py-20 text-center md:px-10 md:py-24">
          <Eyebrow>Lab-grade methods</Eyebrow>
          <h1 className="mx-auto mt-5 max-w-3xl text-4xl font-bold tracking-tight text-white sm:text-5xl">Our Testing</h1>
          <p className="mx-auto mt-4 max-w-2xl text-lg text-slate-300">Objective analysis using the same techniques trusted by accredited analytical laboratories.</p>
        </div>
      </section>

      <Section>
        <div className="grid items-center gap-12 lg:grid-cols-2 lg:gap-20">
          <div>
            <SectionHeading eyebrow="What we measure" title="Real numbers, not marketing"
              subtitle="Every test produces measured values you can read and verify. We never make health claims or recommend products — we simply report the facts." />
          </div>
          <img src="https://images.unsplash.com/photo-1639772823849-6efbd173043c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTJ8MHwxfHNlYXJjaHwzfHxzY2llbmNlJTIwbGFiJTIwdGVzdGluZyUyMGNsZWFufGVufDB8fHx8MTc4MDU1ODk1Nnww&ixlib=rb-4.1.0&q=85"
            alt="Lab testing" className="rounded-3xl border border-border object-cover shadow-[0_8px_30px_rgba(10,61,98,0.08)]" />
        </div>

        <div className="mt-16 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          {TESTS.map((c, i) => (
            <motion.div key={c.t} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.06 }}
              className="group rounded-3xl border border-border bg-white p-7 transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-[0_18px_45px_rgba(47,76,120,0.1)]">
              <span className="grid h-12 w-12 place-items-center rounded-xl bg-accent transition-colors duration-300 group-hover:bg-navy"><c.icon className="h-6 w-6 text-teal-dark transition-colors duration-300 group-hover:text-lime" /></span>
              <h3 className="mt-5 text-lg font-semibold text-ocean">{c.t}</h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-600">{c.d}</p>
            </motion.div>
          ))}
        </div>

        <div className="mt-14 rounded-3xl border border-border bg-white p-8 text-center md:p-12">
          <h3 className="text-2xl font-semibold text-ocean">Want the science behind it?</h3>
          <p className="mx-auto mt-3 max-w-xl text-slate-600">Explore the analytical methods our lab can use to reveal what's inside your supplements.</p>
          <Link to="/testing-technology" data-testid="our-testing-tech-link" className="mt-7 inline-flex items-center gap-2 rounded-full bg-teal px-7 py-3 text-base font-semibold text-white transition-colors hover:bg-teal-dark">
            Our Testing Technology <ArrowRight className="h-5 w-5" />
          </Link>
        </div>
      </Section>
    </div>
  );
}
